//src/pages/personnel/PersonnelCompensationPage.jsx
import React, {
  useMemo,
  useState,
} from "react";

import {
  LuCalendarCheck2,
  LuCalendarDays,
  LuChevronLeft,
  LuChevronRight,
  LuCircleDollarSign,
  LuClipboardCheck,
  LuClock3,
  LuPencil,
  LuPlus,
  LuRefreshCw,
  LuSearch,
  LuTruck,
  LuUser,
} from "react-icons/lu";

import {
  useTranslation,
} from "react-i18next";

import usePersonnelCompensationData from "../../hooks/usePersonnelCompensationData";
import usePersonnelPermissions from "../../hooks/usePersonnelPermissions";

import PersonnelHolidayWorkFormModal from "../../components/personnel/PersonnelHolidayWorkFormModal";

import {
  confirmPersonnelHolidayWork,
} from "../../api/personnelApi";

import {
  markPersonnelResourceStale,
} from "../../utils/personnelCache";

/* =========================================================
 * CONSTANTS
 * ======================================================= */

const COMPENSATION_TYPES = [
  "EXTRA_PAY",
  "TIME_OFF",
  "EXTRA_PAY_AND_TIME_OFF",
  "NONE",
  "CUSTOM",
];

/* =========================================================
 * HELPERS
 * ======================================================= */

const getPersonKey =
  (
    personType,
    personId
  ) =>
    `${personType}:${personId}`;

const normalizeText =
  (
    value
  ) =>
    String(
      value ||
        ""
    )
      .trim()
      .toLocaleLowerCase();

const parseDateOnly =
  (
    value
  ) => {
    const [
      year,
      month,
      day,
    ] =
      String(
        value ||
          ""
      )
        .split("-")
        .map(
          Number
        );

    return new Date(
      year,
      month - 1,
      day
    );
  };

const formatMinutes =
  (
    value,
    t
  ) => {
    const minutes =
      Math.max(
        0,
        Number(
          value
        ) ||
          0
      );

    const hours =
      Math.floor(
        minutes /
          60
      );

    const remainder =
      minutes %
      60;

    const hourUnit =
      t(
        "units.hour_short"
      );

    const minuteUnit =
      t(
        "units.minute_short"
      );

    if (
      hours ===
        0 &&
      remainder ===
        0
    ) {
      return `0${hourUnit}`;
    }

    if (
      remainder ===
      0
    ) {
      return `${hours}${hourUnit}`;
    }

    if (
      hours ===
      0
    ) {
      return `${remainder}${minuteUnit}`;
    }

    return `${hours}${hourUnit} ${remainder}${minuteUnit}`;
  };

/* =========================================================
 * PAGE
 * ======================================================= */

export default function PersonnelCompensationPage() {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "personnel"
    );

  const [
    year,
    setYear,
  ] =
    useState(
      () =>
        new Date()
          .getFullYear()
    );

  const [
    search,
    setSearch,
  ] =
    useState(
      ""
    );

  const [
    statusFilter,
    setStatusFilter,
  ] =
    useState(
      "ALL"
    );

  const [
    personTypeFilter,
    setPersonTypeFilter,
  ] =
    useState(
      "ALL"
    );

  const [
    compensationTypeFilter,
    setCompensationTypeFilter,
  ] =
    useState(
      "ALL"
    );

  const [
    selectedWork,
    setSelectedWork,
  ] =
    useState(
      null
    );

  const [
    workEditor,
    setWorkEditor,
  ] =
    useState(
      null
    );

  const [
    confirmPending,
    setConfirmPending,
  ] =
    useState(
      false
    );

  const [
    actionLoading,
    setActionLoading,
  ] =
    useState(
      false
    );

  const [
    actionError,
    setActionError,
  ] =
    useState(
      null
    );

  const {
    people,
    publicHolidays,
    holidayWork,
    loading,
    refreshing,
    error,
    refresh,
  } =
    usePersonnelCompensationData(
      year
    );

  const {
    compensationCreate,
    compensationEdit,
    compensationConfirm,
    compensationDelete,
    publicHolidaysCreate,
  } =
    usePersonnelPermissions();

  const locale =
    String(
      i18n.resolvedLanguage ||
        i18n.language ||
        "pt"
    );

  /* =======================================================
   * LABELS
   * ===================================================== */

  const compensationTypeLabel =
    (
      type
    ) => {
      const defaults = {
        EXTRA_PAY:
          "Pagamento extra",

        TIME_OFF:
          "Tempo de descanso",

        EXTRA_PAY_AND_TIME_OFF:
          "Pagamento + descanso",

        NONE:
          "Sem compensação",

        CUSTOM:
          "Personalizada",
      };

      return t(
        `compensation_types.${type}`,
        {
          defaultValue:
            defaults[type] ||
            type,
        }
      );
    };

  const statusLabel =
    (
      status
    ) => {
      const defaults = {
        DRAFT:
          "Rascunho",

        CONFIRMED:
          "Confirmado",
      };

      return t(
        `statuses.${status}`,
        {
          defaultValue:
            defaults[
              status
            ] ||
            status,
        }
      );
    };

  /* =======================================================
   * MAPS
   * ===================================================== */

  const personMap =
    useMemo(
      () => {
        const map =
          new Map();

        people.forEach(
          (
            person
          ) => {
            map.set(
              getPersonKey(
                person.personType,
                person.personId
              ),
              person
            );
          }
        );

        return map;
      },
      [
        people,
      ]
    );

  const holidayMap =
    useMemo(
      () => {
        const map =
          new Map();

        publicHolidays.forEach(
          (
            holiday
          ) => {
            map.set(
              Number(
                holiday.id
              ),
              holiday
            );
          }
        );

        return map;
      },
      [
        publicHolidays,
      ]
    );

  /* =======================================================
   * RESOLVED ROWS
   * ===================================================== */

  const rows =
    useMemo(
      () =>
        holidayWork.map(
          (
            work
          ) => {
            const person =
              personMap.get(
                getPersonKey(
                  work.personType,
                  work.personId
                )
              );

            const holiday =
              holidayMap.get(
                Number(
                  work.publicHolidayId
                )
              );

            return {
              ...work,

              person,

              holiday,

              personName:
                person?.displayName ||
                t(
                  "compensation.unknown_person",
                  {
                    defaultValue:
                      "Pessoa desconhecida",
                  }
                ),

              holidayName:
                holiday?.name ||
                t(
                  "compensation.unknown_holiday",
                  {
                    defaultValue:
                      "Feriado",
                  }
                ),
            };
          }
        ),
      [
        holidayWork,
        personMap,
        holidayMap,
        t,
      ]
    );

  /* =======================================================
   * FILTER
   * ===================================================== */

  const filteredRows =
    useMemo(
      () => {
        const needle =
          normalizeText(
            search
          );

        return rows
          .filter(
            (
              row
            ) => {
              if (
                statusFilter !==
                  "ALL" &&
                row.status !==
                  statusFilter
              ) {
                return false;
              }

              if (
                personTypeFilter !==
                  "ALL" &&
                row.personType !==
                  personTypeFilter
              ) {
                return false;
              }

              if (
                compensationTypeFilter !==
                  "ALL" &&
                row.compensationType !==
                  compensationTypeFilter
              ) {
                return false;
              }

              if (!needle) {
                return true;
              }

              const haystack =
                [
                  row.personName,
                  row.holidayName,
                  row.notes,
                  row.compensationType,
                  row.status,
                  row.currency,
                  compensationTypeLabel(
                    row.compensationType
                  ),
                  statusLabel(
                    row.status
                  ),
                ]
                  .map(
                    normalizeText
                  )
                  .join(
                    " "
                  );

              return haystack.includes(
                needle
              );
            }
          )
          .sort(
            (
              first,
              second
            ) =>
              new Date(
                second.startAt
              ).getTime() -
              new Date(
                first.startAt
              ).getTime()
          );
      },
      [
        rows,
        search,
        statusFilter,
        personTypeFilter,
        compensationTypeFilter,
      ]
    );

  /* =======================================================
   * STATS
   * ===================================================== */

  const stats =
    useMemo(
      () => ({
        total:
          holidayWork.length,

        draft:
          holidayWork.filter(
            (
              item
            ) =>
              item.status ===
              "DRAFT"
          ).length,

        confirmed:
          holidayWork.filter(
            (
              item
            ) =>
              item.status ===
              "CONFIRMED"
          ).length,

        workedMinutes:
          holidayWork.reduce(
            (
              total,
              item
            ) =>
              total +
              (
                Number(
                  item.workedMinutes
                ) ||
                0
              ),
            0
          ),
      }),
      [
        holidayWork,
      ]
    );

  /* =======================================================
   * FORMAT
   * ===================================================== */

  const formatDate =
    (
      value
    ) =>
      new Intl.DateTimeFormat(
        locale,
        {
          day:
            "2-digit",

          month:
            "short",

          year:
            "numeric",
        }
      ).format(
        new Date(
          value
        )
      );

  const formatHolidayDate =
    (
      value
    ) =>
      value
        ? new Intl.DateTimeFormat(
            locale,
            {
              day:
                "2-digit",

              month:
                "short",

              year:
                "numeric",
            }
          ).format(
            parseDateOnly(
              value
            )
          )
        : "—";

  const formatDateTime =
    (
      value
    ) =>
      value
        ? new Intl.DateTimeFormat(
            locale,
            {
              day:
                "2-digit",

              month:
                "short",

              year:
                "numeric",

              hour:
                "2-digit",

              minute:
                "2-digit",
            }
          ).format(
            new Date(
              value
            )
          )
        : "—";

  const formatTime =
    (
      value
    ) =>
      value
        ? new Intl.DateTimeFormat(
            locale,
            {
              hour:
                "2-digit",

              minute:
                "2-digit",
            }
          ).format(
            new Date(
              value
            )
          )
        : "—";

  const formatMoney =
    (
      value,
      currency
    ) => {
      const amount =
        Number(
          value
        );

      if (
        !Number.isFinite(
          amount
        )
      ) {
        return "—";
      }

      try {
        return new Intl.NumberFormat(
          locale,
          {
            style:
              "currency",

            currency:
              currency ||
              "EUR",
          }
        ).format(
          amount
        );
      } catch {
        return `${amount.toFixed(
          2
        )} ${currency || "EUR"}`;
      }
    };

  /* =======================================================
   * YEAR
   * ===================================================== */

  const previousYear =
    () => {
      setYear(
        (
          current
        ) =>
          current - 1
      );

      setSelectedWork(
        null
      );
    };

  const nextYear =
    () => {
      setYear(
        (
          current
        ) =>
          current + 1
      );

      setSelectedWork(
        null
      );
    };

  const currentYear =
    () => {
      setYear(
        new Date()
          .getFullYear()
      );

      setSelectedWork(
        null
      );
    };

  /* =======================================================
   * CREATE / EDIT / CONFIRM
   * ===================================================== */

  const openWork =
    (
      work
    ) => {
      setSelectedWork(
        work
      );

      setConfirmPending(
        false
      );

      setActionError(
        null
      );
    };

  const closeWork =
    () => {
      if (
        actionLoading
      ) {
        return;
      }

      setSelectedWork(
        null
      );

      setConfirmPending(
        false
      );

      setActionError(
        null
      );
    };

  const openCreateWork =
    () => {
      setSelectedWork(
        null
      );

      setWorkEditor({
        mode:
          "create",

        work:
          null,
      });
    };

  const openEditWork =
    (
      work
    ) => {
      if (
        work.status !==
        "DRAFT"
      ) {
        return;
      }

      setSelectedWork(
        null
      );

      setWorkEditor({
        mode:
          "edit",

        work,
      });
    };

  const closeWorkEditor =
    () => {
      setWorkEditor(
        null
      );
    };

  const handleWorkChanged =
    async () => {
      await refresh({
        silent:
          true,
      });
    };

  const confirmSelectedWork =
    async () => {
      if (
        !compensationConfirm ||
        !selectedWork ||
        selectedWork.status !==
          "DRAFT"
      ) {
        return;
      }

      setActionLoading(
        true
      );

      setActionError(
        null
      );

      try {
        const updated =
          await confirmPersonnelHolidayWork(
            selectedWork.id
          );

        setSelectedWork(
          (
            current
          ) => ({
            ...current,
            ...updated,
          })
        );

        markPersonnelResourceStale(
          "holidayWork"
        );

        await refresh({
          silent:
            true,
        });

        setConfirmPending(
          false
        );
      } catch (
        confirmError
      ) {
        console.error(
          "[PersonnelCompensation] confirm failed:",
          confirmError
        );

        setActionError(
          confirmError
            ?.response
            ?.data
            ?.error ||
          confirmError
            ?.message ||
          t(
            "compensation.actions.confirm_failed",
            {
              defaultValue:
                "Não foi possível confirmar o registo.",
            }
          )
        );
      } finally {
        setActionLoading(
          false
        );
      }
    };

  /* =======================================================
   * RENDER
   * ===================================================== */

  return (
    <section className="personnel-page personnel-compensation-page">
      <div className="personnel-compensation-stats">
        <div className="personnel-compensation-stat">
          <LuClipboardCheck />

          <div>
            <strong>
              {
                stats.total
              }
            </strong>

            <span>
              {t(
                "compensation.stats.total",
                {
                  defaultValue:
                    "Registos",
                }
              )}
            </span>
          </div>
        </div>

        <div className="personnel-compensation-stat is-worked">
          <LuClock3 />

          <div>
            <strong>
              {formatMinutes(
                stats.workedMinutes,
                t
              )}
            </strong>

            <span>
              {t(
                "compensation.stats.worked",
                {
                  defaultValue:
                    "Trabalho registado",
                }
              )}
            </span>
          </div>
        </div>

        <div className="personnel-compensation-stat is-draft">
          <LuCalendarDays />

          <div>
            <strong>
              {
                stats.draft
              }
            </strong>

            <span>
              {t(
                "compensation.stats.draft",
                {
                  defaultValue:
                    "Rascunhos",
                }
              )}
            </span>
          </div>
        </div>

        <div className="personnel-compensation-stat is-confirmed">
          <LuCalendarCheck2 />

          <div>
            <strong>
              {
                stats.confirmed
              }
            </strong>

            <span>
              {t(
                "compensation.stats.confirmed",
                {
                  defaultValue:
                    "Confirmados",
                }
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="personnel-compensation-toolbar">
        <div className="personnel-compensation-year">
          <button
            type="button"
            onClick={
              previousYear
            }
            aria-label={t(
              "compensation.previous_year"
            )}
          >
            <LuChevronLeft />
          </button>

          <button
            type="button"
            className="personnel-compensation-current-year"
            onClick={
              currentYear
            }
          >
            {t(
              "compensation.current_year",
              {
                defaultValue:
                  "Este ano",
              }
            )}
          </button>

          <button
            type="button"
            onClick={
              nextYear
            }
            aria-label={t(
              "compensation.next_year"
            )}
          >
            <LuChevronRight />
          </button>

          <strong>
            {
              year
            }
          </strong>
        </div>

        <div className="personnel-compensation-toolbar-right">
          <div className="personnel-compensation-search">
            <LuSearch />

            <input
              type="text"
              value={
                search
              }
              onChange={(
                event
              ) =>
                setSearch(
                  event.target
                    .value
                )
              }
              placeholder={t(
                "compensation.search_placeholder",
                {
                  defaultValue:
                    "Pesquisar...",
                }
              )}
            />
          </div>

          <select
            value={
              statusFilter
            }
            onChange={(
              event
            ) =>
              setStatusFilter(
                event.target
                  .value
              )
            }
          >
            <option value="ALL">
              {t(
                "compensation.filters.all_statuses",
                {
                  defaultValue:
                    "Todos os estados",
                }
              )}
            </option>

            <option value="DRAFT">
              {statusLabel(
                "DRAFT"
              )}
            </option>

            <option value="CONFIRMED">
              {statusLabel(
                "CONFIRMED"
              )}
            </option>
          </select>

          <select
            value={
              personTypeFilter
            }
            onChange={(
              event
            ) =>
              setPersonTypeFilter(
                event.target
                  .value
              )
            }
          >
            <option value="ALL">
              {t(
                "compensation.filters.everyone",
                {
                  defaultValue:
                    "Todos",
                }
              )}
            </option>

            <option value="USER">
              {t(
                "requests.filters.users",
                {
                  defaultValue:
                    "Utilizadores",
                }
              )}
            </option>

            <option value="DRIVER">
              {t(
                "requests.filters.drivers",
                {
                  defaultValue:
                    "Motoristas",
                }
              )}
            </option>
          </select>

          <select
            value={
              compensationTypeFilter
            }
            onChange={(
              event
            ) =>
              setCompensationTypeFilter(
                event.target
                  .value
              )
            }
          >
            <option value="ALL">
              {t(
                "compensation.filters.all_types",
                {
                  defaultValue:
                    "Todas as compensações",
                }
              )}
            </option>

            {COMPENSATION_TYPES.map(
              (
                type
              ) => (
                <option
                  key={
                    type
                  }
                  value={
                    type
                  }
                >
                  {compensationTypeLabel(
                    type
                  )}
                </option>
              )
            )}
          </select>

          <div className="personnel-toolbar-actions">
            {compensationCreate && (
              <button
                type="button"
                className="personnel-new-compensation-btn"
                onClick={
                  openCreateWork
                }
              >
                <LuPlus />

                <span>
                  {t(
                    "compensation.form.new_record",
                    {
                      defaultValue:
                        "Novo registo",
                    }
                  )}
                </span>
              </button>
            )}

            <button
              type="button"
              className="personnel-refresh-btn"
              onClick={() =>
                refresh({
                  silent:
                    true,
                })
              }
              disabled={
                refreshing
              }
            >
              <LuRefreshCw
                className={
                  refreshing
                    ? "is-spinning"
                    : ""
                }
              />

              <span>
                {t(
                  "compensation.refresh",
                  {
                    defaultValue:
                      "Atualizar",
                  }
                )}
              </span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="personnel-calendar-warning">
          {t(
            "compensation.refresh_warning",
            {
              defaultValue:
                "Não foi possível atualizar os registos. Os dados em cache continuam visíveis.",
            }
          )}
        </div>
      )}

      <div className="personnel-compensation-table-card">
        <div className="personnel-compensation-table-header">
          <div>
            {t(
              "compensation.columns.person",
              {
                defaultValue:
                  "Pessoa",
              }
            )}
          </div>

          <div>
            {t(
              "compensation.columns.holiday",
              {
                defaultValue:
                  "Feriado",
              }
            )}
          </div>

          <div>
            {t(
              "compensation.columns.period",
              {
                defaultValue:
                  "Horário",
              }
            )}
          </div>

          <div>
            {t(
              "compensation.columns.worked",
              {
                defaultValue:
                  "Trabalhado",
              }
            )}
          </div>

          <div>
            {t(
              "compensation.columns.compensation",
              {
                defaultValue:
                  "Compensação",
              }
            )}
          </div>

          <div>
            {t(
              "compensation.columns.status",
              {
                defaultValue:
                  "Estado",
              }
            )}
          </div>
        </div>

        {loading &&
        holidayWork.length ===
          0 ? (
          Array.from({
            length: 4,
          }).map(
            (
              _,
              index
            ) => (
              <div
                className="personnel-compensation-row personnel-compensation-row--skeleton"
                key={
                  index
                }
              >
                {Array.from({
                  length: 6,
                }).map(
                  (
                    __,
                    cellIndex
                  ) => (
                    <div
                      key={
                        cellIndex
                      }
                    >
                      <span className="personnel-compensation-skeleton" />
                    </div>
                  )
                )}
              </div>
            )
          )
        ) : filteredRows.length ===
          0 ? (
          <div className="personnel-compensation-empty">
            <LuCircleDollarSign />

            <strong>
              {t(
                "compensation.empty.title",
                {
                  defaultValue:
                    "Nenhum registo encontrado",
                }
              )}
            </strong>

            <span>
              {t(
                "compensation.empty.subtitle",
                {
                  defaultValue:
                    "Não existem registos de trabalho em feriados para este ano ou filtros.",
                }
              )}
            </span>
          </div>
        ) : (
          filteredRows.map(
            (
              row
            ) => (
              <button
                type="button"
                className="personnel-compensation-row"
                key={
                  row.id
                }
                onClick={() =>
                  openWork(
                    row
                  )
                }
              >
                <div className="personnel-compensation-person">
                  <span className="personnel-compensation-person-icon">
                    {row.personType ===
                    "DRIVER"
                      ? (
                        <LuTruck />
                      )
                      : (
                        <LuUser />
                      )}
                  </span>

                  <div>
                    <strong>
                      {
                        row.personName
                      }
                    </strong>

                    <span>
                      {row.personType ===
                      "DRIVER"
                        ? t(
                            "requests.filters.drivers",
                            {
                              defaultValue:
                                "Motoristas",
                            }
                          )
                        : t(
                            "requests.filters.users",
                            {
                              defaultValue:
                                "Utilizadores",
                            }
                          )}
                    </span>
                  </div>
                </div>

                <div className="personnel-compensation-holiday">
                  <strong>
                    {
                      row.holidayName
                    }
                  </strong>

                  <span>
                    {formatHolidayDate(
                      row.holiday
                        ?.date
                    )}
                  </span>
                </div>

                <div className="personnel-compensation-period">
                  <strong>
                    {formatTime(
                      row.startAt
                    )}
                    {" → "}
                    {formatTime(
                      row.endAt
                    )}
                  </strong>

                  <span>
                    {formatDate(
                      row.startAt
                    )}
                  </span>
                </div>

                <div>
                  <strong className="personnel-compensation-worked">
                    {formatMinutes(
                      row.workedMinutes,
                      t
                    )}
                  </strong>
                </div>

                <div>
                  <span className="personnel-compensation-type-badge">
                    {compensationTypeLabel(
                      row.compensationType
                    )}
                  </span>
                </div>

                <div>
                  <span
                    className={[
                      "personnel-compensation-status",

                      row.status ===
                      "CONFIRMED"
                        ? "is-confirmed"
                        : "is-draft",
                    ].join(
                      " "
                    )}
                  >
                    {statusLabel(
                      row.status
                    )}
                  </span>
                </div>
              </button>
            )
          )
        )}
      </div>

      <div className="personnel-compensation-footer">
        {t(
          "compensation.showing",
          {
            defaultValue:
              "A mostrar {{shown}} de {{total}} registos em {{year}}",

            shown:
              filteredRows.length,

            total:
              holidayWork.length,

            year,
          }
        )}
      </div>

      {selectedWork && (
        <div
          className="personnel-compensation-overlay"
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
                event.currentTarget
            ) {
              closeWork();
            }
          }}
        >
          <div
            className="personnel-compensation-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="personnel-compensation-modal-header">
              <div>
                <span>
                  {t(
                    "compensation.details.kicker",
                    {
                      defaultValue:
                        "Detalhes da compensação",
                    }
                  )}
                </span>

                <h3>
                  {
                    selectedWork.personName
                  }
                </h3>
              </div>

              <button
                type="button"
                onClick={
                  closeWork
                }
                disabled={
                  actionLoading
                }
                aria-label={t(
                  "common.close"
                )}
              >
                ×
              </button>
            </div>

            <div className="personnel-compensation-modal-summary">
              <span className="personnel-compensation-person-icon">
                {selectedWork.personType ===
                "DRIVER"
                  ? (
                    <LuTruck />
                  )
                  : (
                    <LuUser />
                  )}
              </span>

              <div>
                <strong>
                  {
                    selectedWork.holidayName
                  }
                </strong>

                <span>
                  {formatHolidayDate(
                    selectedWork
                      .holiday
                      ?.date
                  )}
                </span>
              </div>

              <span
                className={[
                  "personnel-compensation-status",

                  selectedWork.status ===
                  "CONFIRMED"
                    ? "is-confirmed"
                    : "is-draft",
                ].join(
                  " "
                )}
              >
                {statusLabel(
                  selectedWork.status
                )}
              </span>
            </div>

            <div className="personnel-compensation-modal-details">
              <div>
                <span>
                  {t(
                    "compensation.details.period",
                    {
                      defaultValue:
                        "Período",
                    }
                  )}
                </span>

                <strong>
                  {formatDateTime(
                    selectedWork.startAt
                  )}
                  {" → "}
                  {formatDateTime(
                    selectedWork.endAt
                  )}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "compensation.details.break",
                    {
                      defaultValue:
                        "Pausa",
                    }
                  )}
                </span>

                <strong>
                  {formatMinutes(
                    selectedWork.breakMinutes,
                    t
                  )}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "compensation.details.worked",
                    {
                      defaultValue:
                        "Tempo trabalhado",
                    }
                  )}
                </span>

                <strong>
                  {formatMinutes(
                    selectedWork.workedMinutes,
                    t
                  )}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "compensation.details.type",
                    {
                      defaultValue:
                        "Compensação",
                    }
                  )}
                </span>

                <strong>
                  {compensationTypeLabel(
                    selectedWork.compensationType
                  )}
                </strong>
              </div>

              {selectedWork.payMultiplier !=
                null && (
                <div>
                  <span>
                    {t(
                      "compensation.details.multiplier",
                      {
                        defaultValue:
                          "Multiplicador",
                      }
                    )}
                  </span>

                  <strong>
                    {
                      selectedWork.payMultiplier
                    }
                    ×
                  </strong>
                </div>
              )}

              {selectedWork.compensationMinutes !=
                null && (
                <div>
                  <span>
                    {t(
                      "compensation.details.compensation_time",
                      {
                        defaultValue:
                          "Tempo de compensação",
                      }
                    )}
                  </span>

                  <strong>
                    {formatMinutes(
                      selectedWork.compensationMinutes,
                      t
                    )}
                  </strong>
                </div>
              )}

              {selectedWork.amountOverride !=
                null && (
                <div>
                  <span>
                    {t(
                      "compensation.details.amount",
                      {
                        defaultValue:
                          "Valor",
                      }
                    )}
                  </span>

                  <strong>
                    {formatMoney(
                      selectedWork.amountOverride,
                      selectedWork.currency
                    )}
                  </strong>
                </div>
              )}

              <div>
                <span>
                  {t(
                    "compensation.details.currency",
                    {
                      defaultValue:
                        "Moeda",
                    }
                  )}
                </span>

                <strong>
                  {selectedWork.currency ||
                    "EUR"}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "compensation.details.notes",
                    {
                      defaultValue:
                        "Notas",
                    }
                  )}
                </span>

                <strong>
                  {selectedWork.notes ||
                    t(
                      "compensation.details.no_notes",
                      {
                        defaultValue:
                          "Sem notas",
                      }
                    )}
                </strong>
              </div>

              {selectedWork.confirmedAt && (
                <div>
                  <span>
                    {t(
                      "compensation.details.confirmed_at",
                      {
                        defaultValue:
                          "Confirmado em",
                      }
                    )}
                  </span>

                  <strong>
                    {formatDateTime(
                      selectedWork.confirmedAt
                    )}
                  </strong>
                </div>
              )}
            </div>

            {actionError && (
              <div className="personnel-compensation-action-error">
                {
                  actionError
                }
              </div>
            )}

            {confirmPending ? (
              <div className="personnel-compensation-confirm">
                <p>
                  {t(
                    "compensation.actions.confirm_question",
                    {
                      defaultValue:
                        "Confirmar definitivamente este registo? Depois de confirmado deixa de poder ser editado ou eliminado.",
                    }
                  )}
                </p>

                <div>
                  <button
                    type="button"
                    className="personnel-request-action-btn is-secondary"
                    onClick={() =>
                      setConfirmPending(
                        false
                      )
                    }
                    disabled={
                      actionLoading
                    }
                  >
                    {t(
                      "requests.actions.back",
                      {
                        defaultValue:
                          "Voltar",
                      }
                    )}
                  </button>

                  <button
                    type="button"
                    className="personnel-compensation-confirm-btn"
                    onClick={
                      confirmSelectedWork
                    }
                    disabled={
                      actionLoading
                    }
                  >
                    <LuCalendarCheck2 />

                    {actionLoading
                      ? t(
                          "requests.actions.working",
                          {
                            defaultValue:
                              "A processar...",
                          }
                        )
                      : t(
                          "compensation.actions.confirm",
                          {
                            defaultValue:
                              "Confirmar registo",
                          }
                        )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="personnel-compensation-modal-actions">
                <div>
                  {selectedWork.status ===
                    "DRAFT" &&
                    compensationEdit && (
                    <button
                      type="button"
                      className="personnel-request-action-btn is-secondary personnel-compensation-edit-btn"
                      onClick={() =>
                        openEditWork(
                          selectedWork
                        )
                      }
                    >
                      <LuPencil />

                      {t(
                        "requests.form.edit",
                        {
                          defaultValue:
                            "Editar",
                        }
                      )}
                    </button>
                  )}
                </div>

                <div>
                  <button
                    type="button"
                    className="personnel-request-action-btn is-secondary"
                    onClick={
                      closeWork
                    }
                  >
                    {t(
                      "requests.actions.close",
                      {
                        defaultValue:
                          "Fechar",
                      }
                    )}
                  </button>

                  {selectedWork.status ===
                    "DRAFT" &&
                    compensationConfirm && (
                    <button
                      type="button"
                      className="personnel-compensation-confirm-btn"
                      onClick={() =>
                        setConfirmPending(
                          true
                        )
                      }
                    >
                      <LuCalendarCheck2 />

                      {t(
                        "compensation.actions.confirm",
                        {
                          defaultValue:
                            "Confirmar registo",
                        }
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {workEditor &&
        (
          workEditor.mode ===
            "create"
            ? compensationCreate
            : compensationEdit
        ) && (
        <PersonnelHolidayWorkFormModal
          key={
            workEditor
              .work
              ?.id ||
            "new-holiday-work"
          }
          year={
            year
          }
          people={
            people
          }
          publicHolidays={
            publicHolidays
          }
          work={
            workEditor
              .work
          }
          canDelete={
            compensationDelete
          }
          canCreateHoliday={
            publicHolidaysCreate
          }
          onClose={
            closeWorkEditor
          }
          onChanged={
            handleWorkChanged
          }
        />
      )}
    </section>
  );
}